import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import apiClient, { API_ENDPOINTS, getUserInfoSync } from '../../config/api'

const getPreferredBranchId = (userInfo) => {
  if (!userInfo) {
    return null
  }
  if (typeof userInfo.branch === 'number') {
    return userInfo.branch
  }
  if (typeof userInfo.branch === 'object' && userInfo.branch !== null) {
    if (typeof userInfo.branch.id === 'number') {
      return userInfo.branch.id
    }
  }
  return (
    userInfo.branch_id ||
    userInfo.assigned_branch_id ||
    userInfo.branchId ||
    null
  )
}

const AddExpense = () => {
  const userInfo = getUserInfoSync()
  const preferredBranchId = useMemo(() => getPreferredBranchId(userInfo), [userInfo])

  const [categories, setCategories] = useState([])
  const [branches, setBranches] = useState([])
  const [warehouses, setWarehouses] = useState([])
  const [loadingLookups, setLoadingLookups] = useState(true)
  const [lookupError, setLookupError] = useState('')

  const [formData, setFormData] = useState({
    category: '',
    branch: preferredBranchId ? String(preferredBranchId) : '',
    warehouse: '',
    amount: '',
    incurred_on: new Date().toISOString().split('T')[0],
    description: '',
    attachment: null
  })

  const [submitting, setSubmitting] = useState(false)
  const fetchLookups = useCallback(async () => {
    try {
      setLoadingLookups(true)
      setLookupError('')
      const [categoryResponse, branchResponse, warehouseResponse] = await Promise.all([
        apiClient.get(API_ENDPOINTS.EXPENSE_CATEGORIES),
        apiClient.get(API_ENDPOINTS.BRANCHES),
        apiClient.get(API_ENDPOINTS.WAREHOUSES)
      ])

      const normalize = (response) => (Array.isArray(response.data) ? response.data : response.data?.results || [])

      setCategories(normalize(categoryResponse))
      setBranches(normalize(branchResponse))
      setWarehouses(normalize(warehouseResponse))
    } catch (error) {
      console.error('Failed to load supporting data for expenses:', error)
      setLookupError(
        error.response?.data?.detail ||
          'Unable to load supporting data. Please refresh the page or try again later.'
      )
    } finally {
      setLoadingLookups(false)
    }
  }, [])

  useEffect(() => {
    fetchLookups()
  }, [fetchLookups])

  const handleInputChange = (event) => {
    const { name, value } = event.target
    setFormData((previous) => ({
      ...previous,
      [name]: value
    }))
  }

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null
    setFormData((previous) => ({
      ...previous,
      attachment: file
    }))
  }

  const resetForm = () => {
    setFormData({
      category: '',
      branch: preferredBranchId ? String(preferredBranchId) : '',
      warehouse: '',
      amount: '',
      incurred_on: new Date().toISOString().split('T')[0],
      description: '',
      attachment: null
    })
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!formData.category) {
      Swal.fire({
        icon: 'error',
        title: 'Category required',
        text: 'Please select a category.'
      })
      return
    }
    if (!formData.amount || Number(formData.amount) <= 0) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid amount',
        text: 'Please capture a valid amount greater than zero.'
      })
      return
    }
    if (!formData.incurred_on) {
      Swal.fire({
        icon: 'error',
        title: 'Date required',
        text: 'Please provide the date the expense was incurred.'
      })
      return
    }

    try {
      setSubmitting(true)

      const payload = new FormData()
      payload.append('category', formData.category)
      if (formData.branch) {
        payload.append('branch', formData.branch)
      }
      if (formData.warehouse) {
        payload.append('warehouse', formData.warehouse)
      }
      payload.append('amount', formData.amount)
      payload.append('incurred_on', formData.incurred_on)
      payload.append('description', formData.description || '')
      if (formData.attachment) {
        payload.append('attachment', formData.attachment)
      }

      await apiClient.post(API_ENDPOINTS.EXPENSES, payload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      Swal.fire({
        icon: 'success',
        title: 'Expense saved',
        text: 'Expense recorded successfully.'
      })
      resetForm()
    } catch (error) {
      console.error('Failed to capture expense:', error)
      const message =
        error.response?.data?.detail ||
        error.response?.data?.amount?.[0] ||
        error.response?.data?.incurred_on?.[0] ||
        'Unable to save expense. Please review the form and try again.'
      Swal.fire({
        icon: 'error',
        title: 'Failed to save',
        text: message
      })
    } finally {
      setSubmitting(false)
    }
  }

  const disabled = submitting || loadingLookups

  return (
    <>
      <div className="pagetitle">
        <h1>Add Expense</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="#">Home</a></li>
            <li className="breadcrumb-item">Expenses</li>
            <li className="breadcrumb-item active">Add Expense</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-xl-8">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                  <div>
                    <h5 className="card-title mb-0">Capture Expense</h5>
                    <small className="text-muted">Attach receipts and tie the cost to a branch/warehouse.</small>
                  </div>
                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={resetForm} disabled={disabled}>
                    Reset
                  </button>
                </div>

                {lookupError && (
                  <div className="alert alert-warning" role="alert">
                    {lookupError}
                  </div>
                )}

                <form className="row g-3" onSubmit={handleSubmit}>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Category</label>
                    <select
                      className="form-select"
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      disabled={disabled}
                      required
                    >
                      <option value="">Select category</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    {!categories.length && !loadingLookups && (
                      <small className="text-muted">No categories yet. Create one first.</small>
                    )}
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-semibold">Branch</label>
                    <select
                      className="form-select"
                      name="branch"
                      value={formData.branch}
                      onChange={handleInputChange}
                      disabled={disabled}
                    >
                      <option value="">Not specified</option>
                      {branches.map((branch) => (
                        <option key={branch.id} value={branch.id}>
                          {branch.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-semibold">Warehouse</label>
                    <select
                      className="form-select"
                      name="warehouse"
                      value={formData.warehouse}
                      onChange={handleInputChange}
                      disabled={disabled}
                    >
                      <option value="">Not specified</option>
                      {warehouses.map((warehouse) => (
                        <option key={warehouse.id} value={warehouse.id}>
                          {warehouse.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Amount</label>
                    <div className="input-group">
                      <span className="input-group-text">$</span>
                      <input
                        type="number"
                        className="form-control"
                        name="amount"
                        min="0"
                        step="0.01"
                        value={formData.amount}
                        onChange={handleInputChange}
                        disabled={disabled}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Date Incurred</label>
                    <input
                      type="date"
                      className="form-control"
                      name="incurred_on"
                      value={formData.incurred_on}
                      onChange={handleInputChange}
                      disabled={disabled}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Attachment</label>
                    <input
                      type="file"
                      className="form-control"
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={handleFileChange}
                      disabled={disabled}
                    />
                    <small className="text-muted">Optional receipt or supporting document.</small>
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold">Description</label>
                    <textarea
                      className="form-control"
                      rows={4}
                      name="description"
                      placeholder="What was this expense for?"
                      value={formData.description}
                      onChange={handleInputChange}
                      disabled={disabled}
                    />
                  </div>
                  <div className="col-12">
                    <button type="submit" className="btn btn-primary" disabled={disabled || !categories.length}>
                      {submitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-save me-2" />
                          Save Expense
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="col-xl-4">
            <div className="card">
              <div className="card-body">
                <h6 className="text-uppercase text-muted fw-semibold mb-3">Why capture every cost?</h6>
                <ul className="small text-muted d-flex flex-column gap-2 mb-0">
                  <li><i className="bi bi-bar-chart-line me-2 text-primary" />Accurate Profit &amp; Loss and cash flow reporting.</li>
                  <li><i className="bi bi-cash-coin me-2 text-success" />Branch managers understand their real margins.</li>
                  <li><i className="bi bi-receipt-cutoff me-2 text-warning" />Audit-friendly history with receipts kept in one place.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default AddExpense
